import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import { H2, SuperHero } from "@bluebottlecoffee/design-system";
import sanityClient from "../../../../lib/sanity-client";
import { BaseProps, filters, queries } from "../../../../lib/sanity/shared";
import { getDefaultStaticPropsWithSlug } from "../../../../lib/utils";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import {
  BrewGuide as BrewGuideSchema,
  Config as ConfigSchema,
} from "../../../../lib/sanity-schema";
import { toSuperHeroProps } from "../../../../lib/transformers/super-hero";
import renderModule from "../../../../lib/render-module";

interface PageData extends BaseProps {
  brewGuide: BrewGuideSchema;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticPropsWithSlug(context);

  const { slug } = defaultProps;

  const brewGuide = await sanityClient().fetch<BrewGuideSchema>(`*[
        ${filters.whereType("brewGuide")}
        && ${filters.matchSlug<BrewGuideSchema>("slug", slug)}
        && ${filters.excludeDrafts}
      ][0]`);

  return {
    props: {
      brewGuide,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const config = await sanityClient().fetch<ConfigSchema>(queries.configQuery);

  const slugs = await sanityClient().fetch<string[]>(
    `*[
        ${filters.whereType("brewGuide")} &&
        ${filters.excludeDrafts}
    ].slug.current`,
  );

  const params = config.languages.flatMap((lang) =>
    slugs.map((slug) => ({
      params: {
        region: config.region,
        lang,
        slug,
      },
    })),
  );

  return {
    paths: params,
    fallback: false,
  };
};

const BrewGuidePage: FunctionComponent<PageData> = ({
  brewGuide,
  ...layoutWrapperProps
}) => {
  const { region, lang } = layoutWrapperProps;

  return (
    <LayoutWrapper
      {...{
        ...layoutWrapperProps,
        pageTitle: brewGuide.title[lang],
        metaTags: brewGuide.metaTags,
      }}
    >
      {brewGuide.hero?.length > 0 && (
        <SuperHero {...toSuperHeroProps(brewGuide.hero, region, lang)} />
      )}

      {brewGuide.modules?.length > 0 &&
        brewGuide.modules.map((m) => renderModule(m, region, lang))}
    </LayoutWrapper>
  );
};

export default BrewGuidePage;
